/**
 * Dashboard layout with sidebar navigation.
 */
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  TrendingUp,
  List,
  MessageSquare,
  Trophy,
  BookOpen,
  Calendar,
  MessageCircle,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Beaker,
  Target,
  Activity,
  BarChart3,
  Bell,
  Radar,
  Shield,
  Zap,
  FlaskConical,
  Calculator,
  History,
  Rocket,
  Stethoscope,
  ArrowDownUp,
  Loader2,
  Twitter,
  SlidersHorizontal,
  FileText,
  Search,
  PieChart,
  ClipboardCheck,
  ShieldCheck,
  Briefcase,
  RefreshCw,
  Scale,
  Dumbbell,
  Wallet,
  Brain,
  Link2,
} from 'lucide-react';
import { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import { Button } from './ui/Button';
import { DeploymentBanner } from './DeploymentBanner';
import { UsageStats } from './UsageStats';
import { SkipLink } from './ui/SkipLink';
import { useCatalystStore } from '@/stores/catalystStore';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import { toast } from './ui/Toast';

// Route chunk preloaders - keyed by path, calls dynamic import to warm browser module cache.
// When React.lazy() later imports the same module, it resolves instantly from cache.
const routePreloaders: Record<string, () => Promise<unknown>> = {
  '/positions': () => import('@/pages/Positions'),
  '/research-workflow': () => import('@/pages/ResearchWorkflow'),
  '/scanner': () => import('@/pages/Scanner'),
  '/performance': () => import('@/pages/Performance'),
  '/trade-tracker': () => import('@/pages/TradeTracker'),
  '/chat': () => import('@/pages/Chat'),
  '/leaderboard': () => import('@/pages/Leaderboard'),
  '/calendar': () => import('@/pages/Calendar'),
  '/feedback': () => import('@/pages/Feedback'),
  '/help': () => import('@/pages/Help'),
  '/post-mortems': () => import('@/pages/PostMortems'),
  '/prompt-lab': () => import('@/pages/PromptVersions'),
  '/shadow-book': () => import('@/pages/ShadowBook'),
  '/unusual-activity': () => import('@/pages/UnusualActivity'),
  '/polymarket': () => import('@/pages/Polymarket'),
  '/catalysts': () => import('@/pages/CatalystTracker'),
  '/tail-risk': () => import('@/pages/TailRisk'),
  '/earnings-alpha': () => import('@/pages/EarningsAlpha'),
  '/valuations': () => import('@/pages/Valuations'),
  '/backtester': () => import('@/pages/Backtester'),
  '/pipeline': () => import('@/pages/AutonomousPipeline'),
  '/trade-doctor': () => import('@/pages/TradeDoctor'),
  '/slippage': () => import('@/pages/SlippageAnalyzer'),
  '/alpha-attribution': () => import('@/pages/AlphaAttribution'),
  '/fintwit-alpha': () => import('@/pages/fintwit'),
  '/performance-attribution': () => import('@/pages/PerformanceAttribution'),
  '/risk-preferences': () => import('@/pages/RiskPreferences'),
  '/tax-summary': () => import('@/pages/TaxSummary'),
  '/portfolio-optimizer': () => import('@/pages/PortfolioOptimizer'),
  '/triage': () => import('@/pages/Triage'),
  '/risk-policies': () => import('@/pages/RiskPolicies'),
  '/dev-studio': () => import('@/pages/DevValue'),
  '/dev-value': () => import('@/pages/DevValue'),
  '/weekly-scorecard': () => import('@/pages/WeeklyScorecard'),
  '/roll-barbell': () => import('@/pages/RollBarbell'),
  '/flow': () => import('@/pages/Flow/FlowPage'),
  '/signals': () => import('@/pages/Signals/SignalsPage'),
  '/model-health': () => import('@/pages/ModelHealth'),
  '/trade-chains': () => import('@/pages/TradeChains'),
  '/risk-pack': () => import('@/pages/RiskPack/RiskPackPage'),
  '/accounts': () => import('@/pages/Accounts'),
};

interface NavItem {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Core',
    items: [
      { path: '/positions', label: 'Positions', icon: LayoutDashboard, adminOnly: true },
      { path: '/research-workflow', label: 'Research', icon: FlaskConical },
      { path: '/scanner', label: 'Scanner', icon: Radar },
      { path: '/performance', label: 'Performance', icon: TrendingUp, adminOnly: true },
    ],
  },
  {
    label: 'Trading',
    items: [
      { path: '/trade-tracker', label: 'Tracker', icon: List },
      { path: '/shadow-book', label: 'Shadow Book', icon: Target },
      { path: '/triage', label: 'Triage', icon: ClipboardCheck },
      { path: '/unusual-activity', label: 'UOA Scanner', icon: Activity },
      { path: '/flow', label: 'Flow', icon: Activity },
      { path: '/signals', label: 'Signals', icon: Radar },
      { path: '/trade-chains', label: 'Trade Chains', icon: Link2 },
      { path: '/earnings-alpha', label: 'Earnings Alpha', icon: Zap },
      { path: '/fintwit-alpha', label: 'FinTwit Alpha', icon: Twitter },
      { path: '/polymarket', label: 'Polymarket', icon: BarChart3 },
      { path: '/catalysts', label: 'Catalysts', icon: Bell },
    ],
  },
  {
    label: 'Analysis',
    items: [
      { path: '/alpha-attribution', label: 'Alpha Proof', icon: Beaker, adminOnly: true },
      { path: '/performance-attribution', label: 'AI Attribution', icon: BarChart3, adminOnly: true },
      { path: '/backtester', label: 'Backtester', icon: History },
      { path: '/valuations', label: 'Valuations', icon: Calculator },
      { path: '/trade-doctor', label: 'Trade Doctor', icon: Stethoscope, adminOnly: true },
      { path: '/roll-barbell', label: 'GOOG Roll', icon: Dumbbell, adminOnly: true },
      { path: '/slippage', label: 'Slippage', icon: ArrowDownUp, adminOnly: true },
      { path: '/weekly-scorecard', label: 'Truth Loop', icon: Scale, adminOnly: true },
      { path: '/tail-risk', label: 'Tail Risk', icon: Shield, adminOnly: true },
      { path: '/portfolio-optimizer', label: 'Optimizer', icon: PieChart, adminOnly: true },
      { path: '/risk-pack', label: 'Risk Pack', icon: Shield, adminOnly: true },
    ],
  },
  {
    label: 'Tools',
    items: [
      { path: '/pipeline', label: 'Pipeline', icon: Rocket },
      { path: '/chat', label: 'Chat', icon: MessageSquare },
      { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
      { path: '/post-mortems', label: 'Post-Mortems', icon: BookOpen },
      { path: '/calendar', label: 'Calendar', icon: Calendar },
      { path: '/risk-preferences', label: 'Risk Settings', icon: SlidersHorizontal },
      { path: '/risk-policies', label: 'Risk Policies', icon: ShieldCheck },
      { path: '/dev-studio', label: 'Dev Studio', icon: Briefcase, adminOnly: true },
      { path: '/broker-sync', label: 'Broker Sync', icon: RefreshCw, adminOnly: true },
      { path: '/accounts', label: 'Cash', icon: Wallet, adminOnly: true },
      { path: '/model-health', label: 'Model Health', icon: Brain, adminOnly: true },
      { path: '/tax-summary', label: 'Tax Summary', icon: FileText },
      { path: '/feedback', label: 'Feedback', icon: MessageCircle },
      { path: '/help', label: 'Help', icon: HelpCircle },
      { path: '/prompt-lab', label: 'Prompt Lab', icon: Beaker, adminOnly: true },
    ],
  },
];

export function DashboardLayout() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Preload route chunk on hover so navigation transitions commit instantly.
  // React Router v7 wraps navigation in startTransition — if the lazy chunk isn't
  // loaded yet, React keeps the old page visible. Preloading eliminates this delay.
  const preloadedRef = useRef<Set<string>>(new Set());
  const handlePreload = useCallback((path: string) => {
    if (preloadedRef.current.has(path)) return;
    preloadedRef.current.add(path);
    routePreloaders[path]?.().catch(() => {
      // Failed preload — remove from set so it can be retried on next hover
      preloadedRef.current.delete(path);
    });
  }, []);

  // Catalyst notification badge
  const catalystStats = useCatalystStore((state) => state.stats);
  const fetchCatalystStats = useCatalystStore((state) => state.fetchStats);
  const unreadTriggerCount = catalystStats?.unread_triggers ?? 0;
  const prevTriggerCountRef = useRef(0);

  // Fetch catalyst stats on mount and poll periodically for notification badge
  // Data lives in Zustand store, not React Query — useEffect + setInterval is correct here
  useEffect(() => {
    fetchCatalystStats();
    const interval = setInterval(fetchCatalystStats, 60_000);
    return () => clearInterval(interval);
  }, [fetchCatalystStats]);

  // Show toast when new triggers are detected
  useEffect(() => {
    if (unreadTriggerCount > prevTriggerCountRef.current && prevTriggerCountRef.current > 0) {
      const newTriggers = unreadTriggerCount - prevTriggerCountRef.current;
      toast.success({
        title: 'Catalyst Triggered',
        description: `${newTriggers} catalyst${newTriggers > 1 ? 's have' : ' has'} triggered! Check the Catalysts page.`,
      });
    }
    prevTriggerCountRef.current = unreadTriggerCount;
  }, [unreadTriggerCount]);

  return (
    <div className="flex h-screen bg-background flex-col">
      {/* Skip link for keyboard navigation */}
      <SkipLink />
      {/* Deployment Banner - shown at very top when on preprod/green environment */}
      <DeploymentBanner />

      <div className="flex flex-1 min-h-0">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close sidebar"
            className="fixed inset-0 bg-black/50 z-40 lg:hidden border-0 p-0"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 bg-card/80 backdrop-blur-sm border-r border-border/60 transform transition-transform lg:translate-x-0 lg:static flex flex-col',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border shrink-0">
            <span className="text-xl font-bold gradient-text font-display">PA Dashboard</span>
            <button
              type="button"
              className="lg:hidden text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* Navigation - scrollable with sections */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-4">
            {navSections.map((section) => {
              const visibleItems = section.items.filter((item) => !item.adminOnly || isAdmin);
              if (visibleItems.length === 0) return null;

              return (
                <div key={section.label}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium px-3 mb-1.5">
                    {section.label}
                  </div>
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;

                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          aria-label={item.label}
                          onClick={() => {
                            handlePreload(item.path);
                            setSidebarOpen(false);
                          }}
                          onMouseEnter={() => handlePreload(item.path)}
                          onFocus={() => handlePreload(item.path)}
                          className={cn(
                            'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-primary/10 text-primary border-l-2 border-primary -ml-[2px]'
                              : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
                          )}
                        >
                          <Icon className="h-4 w-4 transition-colors duration-150 group-hover:text-foreground" />
                          <span className="flex-1">{item.label}</span>
                          {item.path === '/catalysts' && unreadTriggerCount > 0 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full min-w-[1.25rem] text-center">
                              {unreadTriggerCount > 9 ? '9+' : unreadTriggerCount}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* User section - fixed at bottom */}
          <div className="shrink-0 p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-xs font-medium text-primary">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.tier} tier
                </p>
              </div>
            </div>

            {/* Usage Stats */}
            <div className="mb-3 border-b border-border pb-3">
              <UsageStats />
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-card lg:hidden safe-area-top">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            <span className="text-lg font-bold gradient-text font-display">PA Dashboard</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground p-2 -mr-2"
              onClick={() => useCommandPaletteStore.getState().setOpen(true)}
              aria-label="Open command palette"
            >
              <Search className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>

          {/* Page content */}
          {/* No key on Suspense — React Router's <Outlet /> already swaps components per route.
              Adding key={pathname} destroys/recreates the boundary, which deadlocks with
              React Router v7's startTransition-based navigation on lazy routes (BP-075). */}
          <main id="main-content" className="flex-1 overflow-auto safe-area-bottom">
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}
